"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.chatToolRegistry = void 0;
const zod_1 = require("zod");
const attachments_1 = require("../../model/attachments");
const project_iteration_1 = require("../../model/project/project-iteration");
const EXAMPLE_DIFF = {
    id: "diff",
    changes: [
        {
            type: "deleteWorkstream",
            workstreamId: "550e8400-e29b-41d4-a716-446655440000",
        },
        {
            type: "createDeliverable",
            workstreamId: "6ba7b810-9dad-11d1-80b4-00c04fd430c8",
            icon: "Document",
            name: "Sample Deliverable",
            description: "[description]",
        },
        {
            type: "updateDeliverable",
            name: "Conduct 15 Interviews",
            workstreamId: "6ba7b810-9dad-11d1-80b4-00c04fd430c8",
            deliverableId: "7c9e6679-7425-40de-944b-e07fc1f90ae7",
        },
        {
            type: "createWorkstream",
            name: "Sample Workstream",
            description: "Description of the workstream",
            deliverables: [
                {
                    id: "f47ac10b-58cc-4372-a567-0e02b2c3d479",
                    name: "Sample Deliverable 1",
                    description: "Sample description",
                    icon: "Goals",
                },
                {
                    id: "a1b2c3d4-e5f6-7890-abcd-ef1234567890",
                    name: "Sample Deliverable 2",
                    description: "Sample description",
                    icon: "Goals",
                },
            ],
        },
        {
            type: "deleteDeliverable",
            workstreamId: "9a8b7c6d-5e4f-3a2b-1c0d-ef9876543210",
            deliverableId: "b2c3d4e5-f6a7-8901-bcde-f12345678901",
        },
        {
            type: "updateWorkstream",
            workstreamId: "1a2b3c4d-5e6f-7a8b-9c0d-1e2f3a4b5c6d",
            name: "Cross-Functional Collaboration Enhancement",
            description: "Revise the workstream description to emphasize the importance of cross-functional collaboration and stakeholder engagement throughout the project lifecycle.",
        },
    ],
};
const dateFilterSchema = zod_1.z.object({
    startDate: zod_1.z.string().datetime({ offset: true }).optional(),
    endDate: zod_1.z.string().datetime({ offset: true }).optional(),
});
exports.chatToolRegistry = createRegistry({
    createProjectTask: {
        name: "Creating a task",
        description: `Create a task within a specific deliverable.

Select taskType based on what the task produces:
- 'human' for interactive work requiring human action
- 'draft_document' for written deliverables
- 'do_research' for web research tasks
- 'schedule_meeting' for meeting scheduling

IMPORTANT: This tool creates tasks INSIDE existing deliverables.
If you also need to modify workstreams or deliverables (the project structure), those are SEPARATE operations:
1. First: Call "suggestPlanChanges" for structure changes
2. Get user confirmation and apply changes
3. Then: Call "createProjectTask" for task creation

Do NOT try to create tasks within suggestPlanChanges - use this tool instead.`,
        parameters: zod_1.z.object({
            taskName: zod_1.z.string(),
            taskDescription: zod_1.z
                .string()
                .describe("A brief description of the task to be created, this should explain what needs to be done to get the task completed"),
            workstreamId: zod_1.z.string().describe("UUID of the workstream to create the task in"),
            deliverableId: zod_1.z.string().describe("UUID of the deliverable to create the task in"),
            taskType: zod_1.z
                .enum(["human", "schedule_meeting", "draft_document", "do_research"])
                .describe("The task type based on what the task produces or requires.")
                .default("human"),
        }),
        resultSchema: zod_1.z.object({ success: zod_1.z.literal(true) }),
    },
    getMeetingDetails: {
        name: "Retrieving meeting details",
        description: "Retrieve detailed information about a specific meeting within the project, including name, description, transcript and summary.",
        parameters: zod_1.z.object({
            meetingId: zod_1.z.string().describe("The unique identifier of the meeting to retrieve details for."),
        }),
        resultSchema: zod_1.z.object({
            id: zod_1.z.string(),
            title: zod_1.z.string(),
            description: zod_1.z.string().optional(),
            start: zod_1.z.string(),
            end: zod_1.z.string(),
            summary: zod_1.z.string().optional(),
            transcript: zod_1.z.string().optional(),
            inviteeCount: zod_1.z.number(),
        }),
    },
    getOrgMembers: {
        name: "Using organization members",
        description: "Retrieve a list of all members within the organization, including their roles and contact information.",
        parameters: zod_1.z.any(),
        resultSchema: zod_1.z.array(zod_1.z.object({
            id: zod_1.z.string(),
            name: zod_1.z.string(),
            email: zod_1.z.string().email(),
            role: zod_1.z.string().optional(),
        })),
    },
    listProjectAttachments: {
        name: "Using attachments",
        description: "Retrieve a list of attachments associated with the project, including documents, images, and other relevant files.",
        parameters: dateFilterSchema,
        resultSchema: zod_1.z.array(attachments_1.attachmentInfoSchema),
    },
    getAttachmentContent: {
        name: "Reading attachment content",
        description: "Read the content of a project attachment by its ID. Use this to access document contents.",
        parameters: zod_1.z.object({
            attachmentId: zod_1.z.string().describe("The ID of the attachment to read"),
        }),
        resultSchema: zod_1.z.object({
            name: zod_1.z.string(),
            mimeType: zod_1.z.string(),
            content: zod_1.z.string(),
        }),
    },
    getProjectLearnings: {
        name: "Using project learnings",
        description: "Retrieve key learnings and insights from the project to help inform decisions and strategies.",
        parameters: dateFilterSchema,
        resultSchema: zod_1.z.array(zod_1.z.object({
            learning: zod_1.z.string(),
            source: zod_1.z.any(),
            date: zod_1.z.string(),
        })),
    },
    listProjectMeetings: {
        name: "Getting list of project meetings",
        description: "List all meetings associated with the project.",
        parameters: dateFilterSchema,
        resultSchema: zod_1.z.array(zod_1.z.object({
            id: zod_1.z.string(),
            name: zod_1.z.string(),
            description: zod_1.z.string().optional(),
            startDate: zod_1.z.string(),
        })),
    },
    getCompletedDeliverables: {
        name: "Getting completed deliverables",
        description: "Retrieve deliverables that were marked as completed within the project.",
        parameters: dateFilterSchema,
        resultSchema: zod_1.z.array(zod_1.z.object({
            workstreamId: zod_1.z.string(),
            workstreamName: zod_1.z.string(),
            deliverableId: zod_1.z.string(),
            deliverableName: zod_1.z.string(),
            completedAt: zod_1.z.string(),
        })),
    },
    getPlanChanges: {
        name: "Getting plan changes",
        description: "Retrieve changes made to the project plan, including updates to workstreams and deliverables.",
        parameters: dateFilterSchema,
        resultSchema: zod_1.z.array(zod_1.z.object({
            date: zod_1.z.string(),
            reason: zod_1.z.string(),
        })),
    },
    runResearch: {
        name: "Running research",
        description: "Conduct research on behalf of the user to gather information from the internet.",
        parameters: zod_1.z.object({
            query: zod_1.z.string(),
        }),
        resultSchema: zod_1.z.string(),
    },
    suggestMeeting: {
        name: "Suggest a meeting to schedule",
        description: `
This tool should be used to suggest/propose the scheduling of a specific meeting to the user. It does not actually schedule a meeting.

When to use:
- When asked to schedule a meeting you can use this tool to propose a meeting to the user
- When you think it's critical that the user has a meeting to discuss something important, you can suggest meetings as well

Instructions:
- NEVER use anything but user ids for the invitees input. Use the "getOrgMembers" tool to get user ids if you don't have them.
- The meeting description should address the attendees. This will be part of the invite they receive.
- When using this tool say something like "Here is a meeting for you to schedule:" to indicate to the user that they still need to schedule it.
- Do not repeat any of the meeting details after or before suggesting a meeting.
- Do not remind the user that they need to schedule this meeting after suggesting it.
        `,
        parameters: zod_1.z.object({
            meetingTitle: zod_1.z
                .string()
                .describe("Title of the meeting, it's your job to come up with a good title based on the conversation."),
            meetingDescription: zod_1.z
                .string()
                .describe("Description of the meeting, explaining its purpose and agenda, you should also come up with this."),
            durationMinutes: zod_1.z
                .number()
                .min(15)
                .max(60 * 8)
                .describe("Duration of the meeting in minutes, must be between 15 minutes and 8 hours, come up with a reasonable suggestion for the user based on the purpose of the meeting - and be mindful of their time."),
            invitees: zod_1.z.array(zod_1.z.string()).describe("User IDs of the organization members to invite to the meeting."),
        }),
        resultSchema: zod_1.z.object({
            status: zod_1.z.enum(["to_be_scheduled_by_user", "scheduled"]),
        }),
    },
    suggestPlanChanges: {
        name: "Suggesting plan changes",
        description: `Modify project STRUCTURE only (workstreams and deliverables).

VALID CHANGE TYPES (only these 6 are allowed):
- createWorkstream, updateWorkstream, deleteWorkstream
- createDeliverable, updateDeliverable, deleteDeliverable

NOT FOR TASKS. For task operations, use these separate tools:
- createProjectTask - to create tasks
- updateProjectTasks - to modify tasks
- deleteProjectTasks - to remove tasks

WORKFLOW when user wants structure changes AND tasks:
1. Call suggestPlanChanges with workstream/deliverable changes only
2. Ask user to confirm the plan changes
3. After user confirms, call createProjectTask separately for tasks

Example changes format:
\`\`\`json
${JSON.stringify(EXAMPLE_DIFF.changes, null, 2)}
\`\`\`

Do not repeat the changes in plain text - the user can see them. You may explain your reasoning.
After using this tool, always ask user permission before applying with "applyPlanChanges".`,
        parameters: zod_1.z.object({
            reason: zod_1.z.string().describe("Reason for suggesting the plan changes."),
            diff: project_iteration_1.projectDiffSchema.describe("Diff object with changes array. Each change must have a valid type: createWorkstream, updateWorkstream, deleteWorkstream, createDeliverable, updateDeliverable, or deleteDeliverable."),
        }),
        resultSchema: zod_1.z.object({
            suggestionId: zod_1.z.string(),
            status: zod_1.z.enum(["to_be_confirmed"]),
        }),
    },
    applyPlanChanges: {
        name: "Apply suggested plan changes",
        description: `Apply changes previously suggested to the project plan using the "suggestPlanChanges" tool.

After applying changes, this tool automatically checks if any generated documents need updating based on the plan changes.
If stale documents are found, you should offer to update them for the user.`,
        parameters: zod_1.z.object({
            suggestionId: zod_1.z.string().describe("The ID of the plan change suggestion to apply."),
        }),
        resultSchema: zod_1.z.object({
            status: zod_1.z.literal("applied"),
            staleDocuments: zod_1.z
                .object({
                count: zod_1.z.number(),
                documents: zod_1.z.array(zod_1.z.object({
                    id: zod_1.z.string(),
                    name: zod_1.z.string(),
                    reason: zod_1.z.string(),
                })),
            })
                .optional(),
        }),
    },
    listProjectTasks: {
        name: "Listing project tasks",
        description: `List tasks in the project. Use this to find tasks before updating or deleting them.
- Can filter by workstream, deliverable, status, or assignee
- Returns task IDs needed for update/delete operations`,
        parameters: zod_1.z.object({
            workstreamId: zod_1.z.string().optional().describe("Filter by workstream ID"),
            deliverableId: zod_1.z.string().optional().describe("Filter by deliverable ID"),
            status: zod_1.z
                .enum(["done", "started", "on_hold", "pending"])
                .optional()
                .describe("Filter by status: 'done', 'started', 'on_hold', or 'pending' (incomplete)"),
            assignee: zod_1.z.string().optional().describe("Filter by assignee user ID"),
        }),
        resultSchema: zod_1.z.array(zod_1.z.object({
            id: zod_1.z.string(),
            title: zod_1.z.string(),
            description: zod_1.z.string(),
            status: zod_1.z.enum(["done", "started", "on_hold"]).nullable(),
            assignee: zod_1.z.string().nullable().optional(),
            workstreamId: zod_1.z.string(),
            deliverableId: zod_1.z.string().nullable(),
            taskType: zod_1.z.enum(["human", "schedule_meeting", "draft_document", "do_research"]),
        })),
    },
    updateProjectTasks: {
        name: "Updating tasks",
        description: `Update one or more existing tasks in the project.
- Modify task properties: title, description, status, assignee
- Move tasks to a different workstream/deliverable (must provide BOTH workstreamId AND deliverableId)
- Batch multiple updates in a single call for efficiency
- All updates are validated before any changes are made`,
        parameters: zod_1.z.object({
            updates: zod_1.z
                .array(zod_1.z.object({
                taskId: zod_1.z.string().describe("The ID of the task to update"),
                title: zod_1.z.string().optional().describe("New title for the task"),
                description: zod_1.z.string().optional().describe("New description"),
                status: zod_1.z
                    .enum(["done", "started", "on_hold"])
                    .nullable()
                    .optional()
                    .describe("'done', 'started', 'on_hold', or null for to do"),
                assignee: zod_1.z.string().nullable().optional().describe("User ID to assign, or null to unassign"),
                workstreamId: zod_1.z.string().optional().describe("New workstream ID if moving the task"),
                deliverableId: zod_1.z.string().optional().describe("New deliverable ID if moving the task"),
            }))
                .describe("Array of task updates to apply"),
        }),
        resultSchema: zod_1.z.object({
            success: zod_1.z.literal(true),
            updated: zod_1.z.number().describe("Number of tasks updated"),
        }),
    },
    deleteProjectTasks: {
        name: "Deleting tasks",
        description: `Delete one or more tasks from the project.
- Use when tasks are no longer needed or were created in error
- Batch multiple deletions in a single call
- All deletions are validated before any changes are made`,
        parameters: zod_1.z.object({
            taskIds: zod_1.z.array(zod_1.z.string()).describe("Array of task IDs to delete"),
        }),
        resultSchema: zod_1.z.object({
            success: zod_1.z.literal(true),
            deleted: zod_1.z.number().describe("Number of tasks deleted"),
        }),
    },
    getGoogleDriveContext: {
        name: "Searching Google Drive",
        description: `Search the user's Google Drive for relevant files to add to the conversation.

Use this tool when the user asks a question that might be answered by documents in their Google Drive, or when they explicitly mention wanting to reference Drive files.

The user will see suggested files in a panel and can choose which to include.
After surfacing suggestions, stop and let the user review/select them in the panel.
Do NOT answer the substantive question from file titles, metadata, or relevance reasons alone.
Only use Google Drive document contents after the user has added the selected files to the conversation.

Do NOT use this tool for general knowledge questions unrelated to the user's documents.
NEVER show file IDs (externalId) to the user. Only refer to files by their title or name.`,
        parameters: zod_1.z.object({
            query: zod_1.z.string().describe("The search query based on what the user is asking about"),
        }),
        resultSchema: zod_1.z.discriminatedUnion("type", [
            zod_1.z.object({
                type: zod_1.z.literal("file_suggestions"),
                message: zod_1.z.string(),
                files: zod_1.z.array(zod_1.z.object({
                    externalId: zod_1.z.string(),
                    title: zod_1.z.string().nullable(),
                    url: zod_1.z.string().nullable(),
                    mimeType: zod_1.z.string().nullable(),
                    lastModifiedAt: zod_1.z.string().nullable(),
                    relevanceReason: zod_1.z.string(),
                })),
            }),
            zod_1.z.object({
                type: zod_1.z.literal("no_results"),
                message: zod_1.z.string(),
            }),
        ]),
    },
    readGoogleDriveFiles: {
        name: "Reading Google Drive files",
        description: `Read the contents of Google Drive files by their external IDs.

Use this tool AFTER getGoogleDriveContext returns "file_suggestions" and the user asks to import or read those files.
The file IDs come from the "externalId" field in the file_suggestions result.

This tool fetches the actual text content from Google Drive via the Contexto service and returns it directly into the conversation context so you can use it to answer questions.

Do NOT pass Tekkr attachment UUIDs to this tool — only Google Drive external IDs.

NEVER show file IDs (fileId, externalId) to the user. Only refer to files by their name or title.`,
        parameters: zod_1.z.object({
            files: zod_1.z
                .array(zod_1.z.object({
                fileId: zod_1.z.string().describe("The Google Drive external ID (from file_suggestions externalId)"),
                mimeType: zod_1.z.string().optional().describe("MIME type of the file if known"),
                name: zod_1.z.string().optional().describe("File name for reference"),
            }))
                .describe("Array of Google Drive files to read"),
        }),
        resultSchema: zod_1.z.object({
            files: zod_1.z.array(zod_1.z.object({
                fileId: zod_1.z.string(),
                name: zod_1.z.string().nullable(),
                mimeType: zod_1.z.string().nullable(),
                content: zod_1.z.string(),
                error: zod_1.z.string().nullable(),
            })),
        }),
    },
    proposeDocumentUpdate: {
        name: "Proposing document updates",
        description: `Propose edits to a document based on project events and/or information from the chat.

CRITICAL: The attachmentId must be a UUID (e.g., "a1b2c3d4-e5f6-7890-abcd-ef1234567890"). If you don't have the attachment ID, you MUST call listProjectAttachments first to find the document and get its ID. Do NOT use deliverable IDs like "B.1" or "A.2".

IMPORTANT: When the user provides information in the chat that should inform the document update (learnings, Slack messages, images, specific change requests), you MUST include that information in the context parameter. Without it, the tool cannot see what the user shared.

Use tools like getMeetingDetails or getAttachmentContent first if you need additional context.
Only propose updates if changes are actually needed - no changes required is a valid outcome.

After proposing changes, the user will see a visual review dialog in the UI where they can accept/reject individual edits and apply them. Do NOT mention proposal IDs, do NOT ask the user if they want to apply changes - just let them know the proposed changes are ready for their review.`,
        parameters: zod_1.z.object({
            attachmentId: zod_1.z.string().describe("The UUID of the attachment to update (from listProjectAttachments)"),
            context: zod_1.z
                .string()
                .optional()
                .describe("Information from the chat conversation to use when updating the document. Include any user-provided context, learnings, or specific changes requested."),
        }),
        resultSchema: zod_1.z.object({
            proposalId: zod_1.z.string(),
            documentName: zod_1.z.string(),
            edits: zod_1.z.array(zod_1.z.object({
                id: zod_1.z.string(),
                searchText: zod_1.z.string(),
                replaceText: zod_1.z.string(),
                reason: zod_1.z.string(),
                applicable: zod_1.z.boolean(),
            })),
            userChangesDetected: zod_1.z.boolean(),
            changeSummary: zod_1.z.string(),
        }),
    },
});
function createRegistry(input) {
    return input;
}
